import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, downloadFile, downloadFilePost } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, PageBody } from "@/components/PageChrome";
import { Button } from "@/components/controls";
import {
  ScanSearch, RefreshCw, Trash2, X, Download, Package, Truck, ExternalLink,
  Star, Copy, Search, Layers, ChevronDown, ChevronUp, Filter, Rows2, Rows3, GitBranch,
  Languages,
} from "lucide-react";
import { T } from "@/lib/testIds";
import { usePerms } from "@/lib/auth";
import ImageThumb from "@/components/ImageThumb";
import { translateText, detectLang, LANG_FLAG } from "@/lib/translate";
import { useAuth } from "@/lib/auth";

const MARKETPLACES = ["amazon", "flipkart", "myntra", "shopify"];

function StatusBadge({ status }) {
  const map = {
    ok: { bg: "bg-emerald-50", text: "text-emerald-800", border: "border-emerald-200", label: "OK" },
    partial: { bg: "bg-amber-50", text: "text-amber-900", border: "border-amber-300", label: "Partial" },
    failed: { bg: "bg-red-50", text: "text-red-800", border: "border-red-200", label: "Blocked" },
  };
  const t = map[status] || map.partial;
  return <span data-testid={`status-badge-${status || "unknown"}`} className={`inline-flex items-center h-4 px-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${t.bg} ${t.text} ${t.border}`}>{t.label}</span>;
}

function ago(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// --- Column definitions -----------------------------------------------------
const ALL_COLUMNS = [
  { key: "image",         label: "",              width: 40,  compact: true,  align: "center" },
  { key: "asin",          label: "ASIN",          width: 100, compact: true,  mono: true },
  { key: "title",         label: "Title",         width: 340, compact: true },
  { key: "brand",         label: "Brand",         width: 130, compact: true },
  { key: "category",      label: "Category",      width: 200, compact: false },
  { key: "price",         label: "Price",         width: 90,  compact: true,  align: "right", mono: true },
  { key: "mrp",           label: "MRP",           width: 90,  compact: false, align: "right", mono: true },
  { key: "discount",      label: "Disc %",        width: 70,  compact: false, align: "right", mono: true },
  { key: "rating",        label: "Rating",        width: 90,  compact: true,  align: "right", mono: true },
  { key: "rating_count",  label: "# Reviews",     width: 90,  compact: false, align: "right", mono: true },
  { key: "variants",      label: "Variants",      width: 80,  compact: true,  align: "center" },
  { key: "status",        label: "Status",        width: 80,  compact: true,  align: "center" },
  { key: "updated",       label: "Updated",       width: 100, compact: false, mono: true },
];

function getCellValue(r, key) {
  switch (key) {
    case "asin":        return r.asin || "";
    case "title":       return r.title || "";
    case "brand":       return r.brand || "";
    case "category":    return (r.category || []).join(" › ");
    case "price":       return r.price ?? "";
    case "mrp":         return r.mrp ?? "";
    case "discount":    return r.discount_percent ?? "";
    case "rating":      return r.rating ?? "";
    case "rating_count":return r.rating_count ?? "";
    case "variants":    return (r.variants || []).length;
    case "status":      return r.scrape_status || "";
    case "updated":     return r.updated_at || "";
    default:            return "";
  }
}

export default function Listings() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [scrapeUrl, setScrapeUrl] = useState("");
  const [scraping, setScraping] = useState(false);
  const [includeVariants, setIncludeVariants] = useState(false);
  const [detail, setDetail] = useState(null);
  const [convertPreview, setConvertPreview] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [wide, setWide] = useState(false);
  const [sort, setSort] = useState({ key: "updated", dir: "desc" });
  const [colFilters, setColFilters] = useState({}); // { asin: "b0", title: "shoe" }
  const [showFilters, setShowFilters] = useState(true);
  const [showVariantsInList, setShowVariantsInList] = useState(false);
  const [detailVariants, setDetailVariants] = useState(null); // {parent_asin, variants:[]}
  const [variantScraping, setVariantScraping] = useState(false);
  const { canEdit } = usePerms();
  const { user } = useAuth();
  const targetLang = (user?.preferred_language || "en").toLowerCase();
  const [xlate, setXlate] = useState({}); // {title: {text, showing}, description: {text, showing}}
  const [xlatingKey, setXlatingKey] = useState(null);
  const editable = canEdit("listings");

  const load = async () => {
    try {
      const params = { include_variants: showVariantsInList };
      if (q) params.q = q;
      const { data } = await api.get("/listings", { params });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [showVariantsInList]);

  const doScrape = async () => {
    if (!scrapeUrl.trim()) return;
    setScraping(true);
    try {
      const { data } = await api.post("/listings/scrape", {
        url_or_asin: scrapeUrl.trim(),
        save: true,
        include_variants: includeVariants,
      });
      if (data.scrape_status === "failed") {
        toast.error("Amazon blocked the request. Try again in a minute.");
      } else if (data.scrape_status === "partial") {
        toast.warning("Partial data scraped — some fields could not be extracted.");
      } else if (data.variant_listings_count) {
        toast.success(`Scraped ${data.title || data.asin} + ${data.variant_listings_count} variant${data.variant_listings_count > 1 ? "s" : ""}`);
      } else {
        toast.success(`Scraped ${data.title || data.asin}`);
      }
      setScrapeUrl("");
      await load();
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setScraping(false);
  };

  const reScrape = async (rid) => {
    try {
      const { data } = await api.post(`/listings/${rid}/re-scrape`);
      toast.success("Refreshed");
      setDetail(data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const scrapeVariants = async (rid) => {
    setVariantScraping(true);
    try {
      const { data } = await api.post(`/listings/${rid}/scrape-variants`);
      toast.success(`Scraped ${data.variants_scraped} variant${data.variants_scraped === 1 ? "" : "s"}`);
      await loadDetailVariants(rid);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setVariantScraping(false);
  };

  const loadDetailVariants = async (rid) => {
    try {
      const { data } = await api.get(`/listings/${rid}/variants`);
      setDetailVariants(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (rid) => {
    if (!window.confirm("Delete this listing?")) return;
    try {
      await api.delete(`/listings/${rid}`);
      toast.success("Deleted");
      setDetail(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const preview = async (rid, marketplace) => {
    try {
      const { data } = await api.get(`/listings/${rid}/convert/${marketplace}`);
      setConvertPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadOne = (rid, marketplace) => {
    downloadFile(`/listings/${rid}/export/${marketplace}.csv`, `listing-${marketplace}.csv`);
  };

  const bulkExport = async (marketplace) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      await downloadFilePost(
        `/listings/export/${marketplace}.csv`,
        { listing_ids: ids },
        `listings-${marketplace}-${ids.length}.csv`,
      );
      toast.success(`Exported ${ids.length} listing${ids.length === 1 ? "" : "s"} for ${marketplace}`);
    } catch (e) { toast.error(e.message || "Bulk export failed"); }
  };

  const importToInventory = async (rid) => {
    const warehouse_name = (window.prompt("Warehouse for this inventory row (e.g. NOIDA)?") || "").trim();
    if (!warehouse_name) return;
    try {
      const { data } = await api.post(`/listings/${rid}/import-to-inventory`, { warehouse_name });
      toast.success(data.created ? `Inventory row created for ${data.system_sku}` : `SKU ${data.system_sku} already exists`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const importToProcurement = async (rid) => {
    try {
      await api.post(`/listings/${rid}/import-to-procurement`);
      toast.success("Procurement entry created");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // --- Filtering / sorting -------------------------------------------------
  const visibleColumns = useMemo(
    () => (wide ? ALL_COLUMNS : ALL_COLUMNS.filter((c) => c.compact)),
    [wide]
  );

  const filteredRows = useMemo(() => {
    const gs = q.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (gs) {
        const hay = [r.title, r.asin, r.brand, (r.category || []).join(" ")].join(" ").toLowerCase();
        if (!hay.includes(gs)) return false;
      }
      for (const [k, v] of Object.entries(colFilters)) {
        if (!v) continue;
        const cell = String(getCellValue(r, k) ?? "").toLowerCase();
        if (!cell.includes(String(v).toLowerCase())) return false;
      }
      return true;
    });
    out.sort((a, b) => {
      const av = getCellValue(a, sort.key);
      const bv = getCellValue(b, sort.key);
      const an = typeof av === "number" ? av : parseFloat(av);
      const bn = typeof bv === "number" ? bv : parseFloat(bv);
      let cmp;
      if (!isNaN(an) && !isNaN(bn) && av !== "" && bv !== "") cmp = an - bn;
      else cmp = String(av).localeCompare(String(bv));
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return out;
  }, [rows, q, colFilters, sort]);

  const allSelected = filteredRows.length > 0 && filteredRows.every((r) => selected.has(r.id));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) filteredRows.forEach((r) => next.delete(r.id));
      else filteredRows.forEach((r) => next.add(r.id));
      return next;
    });
  };
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const openDetail = async (r) => {
    try {
      const { data } = await api.get(`/listings/${r.id}`);
      setDetail(data);
      setXlate({});
      loadDetailVariants(data.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const translateField = async (key, text) => {
    if (!text) return;
    const existing = xlate[key];
    if (existing?.text) {
      setXlate((p) => ({ ...p, [key]: { ...p[key], showing: !p[key].showing } }));
      return;
    }
    setXlatingKey(key);
    try {
      const r = await translateText(text, targetLang);
      setXlate((p) => ({ ...p, [key]: { text: r.translated_text, showing: true } }));
    } catch (_e) {
      toast.error("Translation failed");
    }
    setXlatingKey(null);
  };

  const setSortKey = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  };

  return (
    <>
      <PageHeader title="Listings" subtitle="Scrape Amazon products • Bulk convert to Amazon / Flipkart / Myntra / Shopify templates" testId={T.moduleTitle("listings")} module="listings">
        <div className="flex items-center gap-1.5">
          <button
            data-testid="listings-toggle-wide"
            onClick={() => setWide((w) => !w)}
            title={wide ? "Switch to compact view" : "Switch to wide view"}
            className={`h-8 px-2 inline-flex items-center gap-1 border rounded-sm text-[11.5px] font-semibold ${wide ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
          >
            {wide ? <Rows3 size={12} /> : <Rows2 size={12} />} {wide ? "Wide" : "Compact"}
          </button>
          <button
            data-testid="listings-toggle-filters"
            onClick={() => setShowFilters((s) => !s)}
            title="Toggle column filters"
            className={`h-8 px-2 inline-flex items-center gap-1 border rounded-sm text-[11.5px] font-semibold ${showFilters ? "bg-indigo-600 text-white border-indigo-600" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
          >
            <Filter size={12} /> Filters
          </button>
          <button
            data-testid="listings-toggle-variants-list"
            onClick={() => setShowVariantsInList((s) => !s)}
            title={showVariantsInList ? "Hide variant rows" : "Show variant rows"}
            className={`h-8 px-2 inline-flex items-center gap-1 border rounded-sm text-[11.5px] font-semibold ${showVariantsInList ? "bg-amber-500 text-white border-amber-500" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
          >
            <GitBranch size={12} /> {showVariantsInList ? "Incl. variants" : "Parents only"}
          </button>
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid={T.searchInput("listings")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Global search…"
              className="h-8 pl-7 pr-2 w-56 text-[13px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
      </PageHeader>
      <PageBody>
        {/* Scraper input strip */}
        {editable && (
          <div className="mb-3 bg-white border border-slate-200 rounded-sm p-2.5 flex flex-wrap gap-2 items-center">
            <ScanSearch size={15} className="text-indigo-600" />
            <span className="text-[12px] font-semibold text-slate-700">Scrape Amazon:</span>
            <input
              data-testid="listings-scrape-input"
              value={scrapeUrl}
              onChange={(e) => setScrapeUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doScrape()}
              placeholder="Paste Amazon URL or ASIN (e.g. B07XG5KLM3)"
              className="flex-1 min-w-[280px] h-8 px-2 text-[13px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
            />
            <label className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-slate-700 select-none px-2 h-8 border border-slate-200 rounded-sm bg-slate-50 cursor-pointer">
              <input
                type="checkbox"
                data-testid="listings-include-variants"
                checked={includeVariants}
                onChange={(e) => setIncludeVariants(e.target.checked)}
                className="accent-indigo-600"
              />
              <Layers size={12} /> All variants
            </label>
            <Button variant="primary" onClick={doScrape} disabled={scraping || !scrapeUrl.trim()} testId="listings-scrape-btn">
              {scraping ? <><RefreshCw size={13} className="animate-spin" /> Scraping…</> : <><ScanSearch size={13} /> Scrape</>}
            </Button>
          </div>
        )}

        {/* Data grid */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="overflow-auto max-h-[calc(100vh-220px)]">
            <table className="erp-table w-full" data-testid="listings-grid">
              <thead className="sticky top-0 z-10 bg-slate-100">
                <tr>
                  <th style={{ width: 32 }} className="text-center">
                    <input
                      type="checkbox"
                      data-testid="listings-select-all"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="accent-indigo-600"
                    />
                  </th>
                  {visibleColumns.map((c) => (
                    <th
                      key={c.key}
                      style={{ width: c.width, minWidth: c.width, textAlign: c.align || "left" }}
                      className="cursor-pointer select-none hover:bg-slate-200"
                      onClick={() => c.key !== "image" && setSortKey(c.key)}
                      data-testid={`listings-col-header-${c.key}`}
                    >
                      <span className="inline-flex items-center gap-0.5">
                        {c.label}
                        {sort.key === c.key && c.key !== "image" && (
                          sort.dir === "asc" ? <ChevronUp size={10} /> : <ChevronDown size={10} />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
                {showFilters && (
                  <tr className="bg-white border-t border-slate-200">
                    <th />
                    {visibleColumns.map((c) => (
                      <th key={c.key} style={{ width: c.width }} className="p-0.5">
                        {c.key === "image" || c.key === "status" ? null : (
                          <input
                            data-testid={`listings-filter-${c.key}`}
                            value={colFilters[c.key] || ""}
                            onChange={(e) => setColFilters({ ...colFilters, [c.key]: e.target.value })}
                            placeholder="filter…"
                            className="w-full h-6 px-1.5 text-[11px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        )}
                      </th>
                    ))}
                  </tr>
                )}
              </thead>
              <tbody>
                {filteredRows.map((r) => {
                  const isVariant = !!r.variant_of;
                  const rowSelected = selected.has(r.id);
                  return (
                    <tr
                      key={r.id}
                      data-testid={`listings-row-${r.id}`}
                      className={`border-t border-slate-100 hover:bg-indigo-50/40 ${rowSelected ? "bg-blue-50/60" : ""} ${isVariant ? "bg-amber-50/30" : ""}`}
                      onClick={() => openDetail(r)}
                    >
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          data-testid={`listings-row-select-${r.id}`}
                          checked={rowSelected}
                          onChange={() => toggleOne(r.id)}
                          className="accent-indigo-600"
                        />
                      </td>
                      {visibleColumns.map((c) => {
                        const align = c.align || "left";
                        const mono = c.mono ? "font-mono" : "";
                        const common = `text-[12px] ${mono}`;
                        switch (c.key) {
                          case "image":
                            return (
                              <td key={c.key} className="p-1">
                                {(r.images && r.images[0]) ? (
                                  <ImageThumb src={r.images[0]} size={64} fit="contain" testId={`listing-thumb-${r.id}`} />
                                ) : (
                                  <div className="w-8 h-8 bg-slate-50 border border-slate-100 rounded-sm" />
                                )}
                              </td>
                            );
                          case "asin":
                            return (
                              <td key={c.key} className={common}>
                                <div className="flex items-center gap-1">
                                  {isVariant && <span title="Variant" className="text-amber-600"><GitBranch size={10} /></span>}
                                  <span>{r.asin}</span>
                                </div>
                              </td>
                            );
                          case "title":
                            return (
                              <td key={c.key} className="text-[12px] font-medium text-slate-800 truncate max-w-[340px]" title={r.title || ""}>
                                {r.title || <span className="text-slate-400 italic">Untitled</span>}
                                {r.variant_label && <span className="ml-1 text-[10px] text-amber-700 font-mono">[{r.variant_label}]</span>}
                              </td>
                            );
                          case "brand":
                            return <td key={c.key} className={common}>{r.brand || "—"}</td>;
                          case "category":
                            return <td key={c.key} className="text-[11.5px] text-slate-600 truncate max-w-[200px]" title={(r.category || []).join(" › ")}>{(r.category || []).join(" › ") || "—"}</td>;
                          case "price":
                            return <td key={c.key} className={`${common} text-right font-semibold`}>{r.price != null ? `₹${r.price}` : "—"}</td>;
                          case "mrp":
                            return <td key={c.key} className={`${common} text-right text-slate-500 line-through`}>{r.mrp != null ? `₹${r.mrp}` : ""}</td>;
                          case "discount":
                            return <td key={c.key} className={`${common} text-right ${r.discount_percent ? "text-emerald-700" : "text-slate-400"}`}>{r.discount_percent ? `${r.discount_percent}%` : "—"}</td>;
                          case "rating":
                            return (
                              <td key={c.key} className={`${common} text-right`}>
                                {r.rating ? (
                                  <span className="inline-flex items-center gap-0.5 text-amber-700">
                                    <Star size={9} className="fill-amber-500 text-amber-500" />{r.rating}
                                  </span>
                                ) : "—"}
                              </td>
                            );
                          case "rating_count":
                            return <td key={c.key} className={`${common} text-right text-slate-600`}>{r.rating_count ?? "—"}</td>;
                          case "variants":
                            return (
                              <td key={c.key} className="text-[11.5px] text-center">
                                {(r.variants || []).length ? (
                                  <span className="inline-flex items-center gap-0.5 text-indigo-700 font-mono">
                                    <Layers size={10} />{(r.variants || []).length}
                                  </span>
                                ) : <span className="text-slate-300">—</span>}
                              </td>
                            );
                          case "status":
                            return <td key={c.key} className="text-center"><StatusBadge status={r.scrape_status} /></td>;
                          case "updated":
                            return <td key={c.key} className="text-[11px] font-mono text-slate-500">{ago(r.updated_at)}</td>;
                          default:
                            return <td key={c.key} className={common} />;
                        }
                      })}
                    </tr>
                  );
                })}
                {!filteredRows.length && (
                  <tr>
                    <td colSpan={visibleColumns.length + 1} className="p-10 text-center text-slate-500 text-[13px] border-t border-slate-100">
                      {rows.length === 0
                        ? "No listings yet. Paste an Amazon URL or ASIN above to scrape your first product."
                        : "No rows match the current filters."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer / bulk action bar */}
        <div className="mt-2 flex items-center justify-between text-[11.5px] text-slate-600">
          <div className="font-mono" data-testid="listings-row-count">
            {filteredRows.length} row{filteredRows.length === 1 ? "" : "s"}
            {selected.size > 0 && <span className="ml-2 text-indigo-700 font-semibold">• {selected.size} selected</span>}
          </div>
          {selected.size > 0 && (
            <div className="flex items-center gap-2" data-testid="listings-bulk-action-bar">
              <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mr-1">Bulk export:</span>
              {MARKETPLACES.map((m) => (
                <button
                  key={m}
                  onClick={() => bulkExport(m)}
                  data-testid={`listings-bulk-export-${m}`}
                  className="h-7 px-2 inline-flex items-center gap-1 border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 rounded-sm text-[11.5px] font-semibold capitalize"
                >
                  <Download size={11} /> {m}
                </button>
              ))}
              <button
                onClick={clearSelection}
                data-testid="listings-clear-selection"
                className="h-7 px-2 inline-flex items-center gap-1 border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 rounded-sm text-[11.5px]"
              >
                <X size={11} /> Clear
              </button>
            </div>
          )}
        </div>
      </PageBody>

      {/* Detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40" onClick={() => { setDetail(null); setDetailVariants(null); }}>
          <div className="w-[900px] max-w-full h-full bg-white border-l border-slate-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="h-14 border-b border-slate-200 flex items-center justify-between px-4 shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <StatusBadge status={detail.scrape_status} />
                <div className="min-w-0">
                  {(() => {
                    const t = detail.title || "Untitled";
                    const src = detectLang(t);
                    const showX = xlate.title?.showing && xlate.title?.text;
                    return (
                      <div className="flex items-center gap-2">
                        <div className="font-heading text-[14px] font-semibold text-slate-900 truncate">
                          {showX ? xlate.title.text : t}
                        </div>
                        {src !== targetLang && t !== "Untitled" && (
                          <button
                            onClick={() => translateField("title", t)}
                            disabled={xlatingKey === "title"}
                            data-testid="listings-translate-title"
                            className="inline-flex items-center gap-0.5 text-[10px] text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50 shrink-0"
                          >
                            {xlatingKey === "title" ? <RefreshCw size={9} className="animate-spin" /> : <Languages size={9} />}
                            {showX ? LANG_FLAG[src] : LANG_FLAG[targetLang]}
                          </button>
                        )}
                      </div>
                    );
                  })()}
                  <div className="font-mono text-[11px] text-slate-500">
                    {detail.asin}
                    {detail.variant_of && (
                      <span className="ml-2 text-amber-700">
                        <GitBranch size={9} className="inline" /> variant of {detail.variant_of}
                      </span>
                    )}
                    <a href={detail.url} target="_blank" rel="noreferrer" className="ml-2 text-blue-600 hover:underline inline-flex items-center gap-0.5">Amazon <ExternalLink size={9} /></a>
                  </div>
                </div>
              </div>
              <button onClick={() => { setDetail(null); setDetailVariants(null); }} data-testid="listings-detail-close" className="text-slate-500 hover:text-slate-900"><X size={16} /></button>
            </div>

            {/* Action bar */}
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 flex flex-wrap items-center gap-2 shrink-0">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mr-1">Export to:</span>
              {MARKETPLACES.map((m) => (
                <div key={m} className="inline-flex border border-slate-200 bg-white rounded-sm overflow-hidden">
                  <button
                    onClick={() => preview(detail.id, m)}
                    data-testid={`listings-preview-${m}`}
                    className="h-7 px-2 text-[11.5px] font-semibold text-slate-700 hover:bg-slate-50 capitalize border-r border-slate-200"
                  >
                    {m}
                  </button>
                  <button
                    onClick={() => downloadOne(detail.id, m)}
                    data-testid={`listings-download-${m}`}
                    className="h-7 w-7 flex items-center justify-center text-blue-700 hover:bg-blue-50"
                    title={`Download ${m} CSV`}
                  >
                    <Download size={12} />
                  </button>
                </div>
              ))}
              <div className="mx-2 h-5 border-l border-slate-200" />
              <Button onClick={() => importToInventory(detail.id)} testId="listings-import-inventory">
                <Package size={12} /> Add to Inventory
              </Button>
              <Button onClick={() => importToProcurement(detail.id)} testId="listings-import-procurement">
                <Truck size={12} /> Add to Procurement
              </Button>
              <div className="ml-auto flex items-center gap-2">
                {editable && (
                  <button onClick={() => reScrape(detail.id)} className="text-[11.5px] text-blue-600 hover:underline inline-flex items-center gap-1" data-testid="listings-rescrape">
                    <RefreshCw size={11} /> Re-scrape
                  </button>
                )}
                {editable && (
                  <button onClick={() => remove(detail.id)} className="text-[11.5px] text-red-600 hover:underline inline-flex items-center gap-1" data-testid="listings-delete">
                    <Trash2 size={11} /> Delete
                  </button>
                )}
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-auto">
              {detail.scrape_error && (
                <div className="m-4 p-3 border border-amber-300 bg-amber-50 rounded-sm text-[12px] text-amber-900">
                  <strong>Scraper note:</strong> {detail.scrape_error}
                </div>
              )}

              {/* Variants section */}
              {((detail.variants || []).length > 0 || (detailVariants?.variants || []).length > 0 || !detail.variant_of) && (
                <div className="p-4 border-b border-slate-200 bg-amber-50/30">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10.5px] uppercase tracking-wider text-slate-600 font-semibold inline-flex items-center gap-1">
                      <GitBranch size={11} /> Variants
                      {(detail.variants || []).length > 0 && (
                        <span className="ml-1 font-mono text-slate-500">({(detail.variants || []).length} detected)</span>
                      )}
                    </div>
                    {editable && (detail.variants || []).length > 0 && !detail.variant_of && (
                      <button
                        onClick={() => scrapeVariants(detail.id)}
                        disabled={variantScraping}
                        data-testid="listings-scrape-variants"
                        className="h-7 px-2 inline-flex items-center gap-1 text-[11.5px] font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-sm disabled:opacity-50"
                      >
                        {variantScraping ? <><RefreshCw size={11} className="animate-spin" /> Scraping…</> : <><Layers size={11} /> Scrape all variants</>}
                      </button>
                    )}
                  </div>
                  {(detail.variants || []).length === 0 && !detail.variant_of ? (
                    <div className="text-[12px] text-slate-500 italic">No variants detected for this product.</div>
                  ) : (
                    <table className="erp-table w-full">
                      <thead>
                        <tr>
                          <th className="text-left" style={{ width: 100 }}>ASIN</th>
                          <th className="text-left">Label</th>
                          <th className="text-right" style={{ width: 90 }}>Price</th>
                          <th className="text-center" style={{ width: 60 }}>Saved</th>
                          <th style={{ width: 40 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detail.variants || []).map((v, i) => {
                          const savedRow = (detailVariants?.variants || []).find((x) => x.asin === v.asin);
                          return (
                            <tr key={v.asin || i} data-testid={`listings-variant-${v.asin}`} className="border-t border-slate-100">
                              <td className="font-mono text-[11px]">{v.asin}</td>
                              <td className="text-[12px]">{v.label || "—"}</td>
                              <td className="text-right font-mono text-[11.5px]">{v.price ? (typeof v.price === "number" ? `₹${v.price}` : v.price) : (savedRow?.price ? `₹${savedRow.price}` : "—")}</td>
                              <td className="text-center">
                                {savedRow
                                  ? <span className="text-emerald-700 text-[10px] font-bold">✓</span>
                                  : <span className="text-slate-300 text-[10px]">—</span>}
                              </td>
                              <td className="text-right">
                                {savedRow && (
                                  <button
                                    onClick={() => openDetail(savedRow)}
                                    className="text-[10.5px] text-blue-600 hover:underline"
                                    data-testid={`listings-variant-open-${v.asin}`}
                                  >
                                    open
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Images grid */}
              {(detail.images || []).length > 0 && (
                <div className="p-4 border-b border-slate-200">
                  <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Images ({detail.images.length})</div>
                  <div className="flex gap-2 overflow-x-auto">
                    {detail.images.map((u, i) => (
                      <a key={i} href={u} target="_blank" rel="noreferrer" className="w-24 h-24 shrink-0 bg-slate-50 border border-slate-200 rounded-sm overflow-hidden flex items-center justify-center hover:border-indigo-300">
                        <img referrerPolicy="no-referrer" src={u} alt="" className="max-w-full max-h-full object-contain" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Facts strip */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-4 border-b border-slate-200 bg-slate-50">
                <div className="bg-white p-2 border border-slate-200 rounded-sm">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Brand</div>
                  <div className="font-mono text-[13px] font-semibold text-slate-900 truncate">{detail.brand || "—"}</div>
                </div>
                <div className="bg-white p-2 border border-slate-200 rounded-sm">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Selling price</div>
                  <div className="font-mono text-[13px] font-semibold text-emerald-800">₹{detail.price ?? "—"}</div>
                </div>
                <div className="bg-white p-2 border border-slate-200 rounded-sm">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">MRP</div>
                  <div className="font-mono text-[13px] font-semibold text-slate-700"><span className="line-through">{detail.mrp ? `₹${detail.mrp}` : "—"}</span> {detail.discount_percent ? <span className="ml-1 text-emerald-700">-{detail.discount_percent}%</span> : ""}</div>
                </div>
                <div className="bg-white p-2 border border-slate-200 rounded-sm">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Rating</div>
                  <div className="font-mono text-[13px] font-semibold text-amber-700">{detail.rating ? `${detail.rating} ★ (${detail.rating_count || 0})` : "—"}</div>
                </div>
              </div>

              {/* Category */}
              {(detail.category || []).length > 0 && (
                <div className="p-4 border-b border-slate-200">
                  <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Category</div>
                  <div className="text-[12.5px] text-slate-800">{detail.category.join(" › ")}</div>
                </div>
              )}

              {/* Bullets */}
              {(detail.bullets || []).length > 0 && (
                <div className="p-4 border-b border-slate-200">
                  <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Bullet points ({detail.bullets.length})</div>
                  <ul className="list-disc pl-5 space-y-1 text-[12.5px] text-slate-800">
                    {detail.bullets.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </div>
              )}

              {/* Description */}
              {detail.description && (() => {
                const src = detectLang(detail.description);
                const showX = xlate.description?.showing && xlate.description?.text;
                const canX = src !== targetLang;
                return (
                  <div className="p-4 border-b border-slate-200">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Description</div>
                      {canX && (
                        <button
                          onClick={() => translateField("description", detail.description)}
                          disabled={xlatingKey === "description"}
                          data-testid="listings-translate-description"
                          className="inline-flex items-center gap-1 text-[10.5px] text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50"
                        >
                          {xlatingKey === "description" ? <RefreshCw size={10} className="animate-spin" /> : <Languages size={10} />}
                          {showX ? `Show ${LANG_FLAG[src]}` : `Translate → ${LANG_FLAG[targetLang]}`}
                        </button>
                      )}
                    </div>
                    <div className="text-[12.5px] text-slate-800 whitespace-pre-line max-h-56 overflow-auto">
                      {showX ? xlate.description.text : detail.description}
                    </div>
                  </div>
                );
              })()}

              {/* Specifications */}
              {Object.keys(detail.specifications || {}).length > 0 && (
                <div className="p-4 border-b border-slate-200">
                  <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Specifications ({Object.keys(detail.specifications).length})</div>
                  <table className="erp-table">
                    <tbody>
                      {Object.entries(detail.specifications).map(([k, v]) => (
                        <tr key={k}>
                          <td className="font-mono text-[11.5px] text-slate-600 w-56">{k}</td>
                          <td className="text-[12.5px]">{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Reviews */}
              {(detail.reviews || []).length > 0 && (
                <div className="p-4 border-b border-slate-200">
                  <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Recent reviews ({detail.reviews.length})</div>
                  <div className="space-y-2">
                    {detail.reviews.map((r, i) => (
                      <div key={i} className="border border-slate-200 rounded-sm p-2">
                        <div className="flex items-center justify-between text-[11.5px]">
                          <span className="font-semibold text-slate-900">{r.title || "—"}</span>
                          <span className="text-amber-700 font-mono">{r.rating ? `${r.rating} ★` : ""}</span>
                        </div>
                        <div className="text-[11.5px] text-slate-500 mb-1">{r.author}</div>
                        <div className="text-[12px] text-slate-700 whitespace-pre-line line-clamp-3">{r.body}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Convert preview modal */}
      {convertPreview && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50" onClick={() => setConvertPreview(null)}>
          <div className="w-[900px] max-w-[95vw] max-h-[85vh] bg-white border border-slate-200 rounded-sm flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="h-12 border-b border-slate-200 px-4 flex items-center justify-between">
              <div className="font-heading text-[13.5px] font-semibold text-slate-900 capitalize">
                {convertPreview.marketplace} template preview
                <span className="ml-2 text-[11px] text-slate-500 font-normal font-mono">{convertPreview.row_count} row{convertPreview.row_count > 1 ? "s" : ""}</span>
              </div>
              <button onClick={() => setConvertPreview(null)} data-testid="listings-preview-close" className="text-slate-500 hover:text-slate-900"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-auto">
              <table className="erp-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: 200 }}>Column</th>
                    {convertPreview.rows.map((_, i) => <th key={i}>Row {i + 1}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {Array.from(new Set(convertPreview.rows.flatMap((r) => Object.keys(r)))).map((col) => (
                    <tr key={col}>
                      <td className="font-mono text-[11px] text-slate-600">{col}</td>
                      {convertPreview.rows.map((r, i) => (
                        <td key={i} className="text-[11.5px] max-w-[320px] truncate" title={String(r[col] ?? "")}>
                          {String(r[col] ?? "")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="h-12 border-t border-slate-200 px-4 flex items-center justify-end gap-2">
              <button onClick={() => {
                const cols = Array.from(new Set(convertPreview.rows.flatMap((r) => Object.keys(r))));
                const csv = [cols.join(",")]
                  .concat(convertPreview.rows.map((r) => cols.map((k) => JSON.stringify(r[k] ?? "")).join(",")))
                  .join("\n");
                navigator.clipboard.writeText(csv);
                toast.success("CSV copied to clipboard");
              }} className="text-[11.5px] text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"><Copy size={11} /> Copy CSV</button>
              <Button onClick={() => setConvertPreview(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
